// useCoinsPerfil.js — quanto cada campo do perfil AINDA rende em Insigne Coins.
//
// POR QUE EXISTE: o "+10" ao lado do campo precisa aparecer em duas telas
// distantes uma da outra (Dados Pessoais e Endereço de Entrega) e as duas
// precisam da MESMA resposta do servidor. Sem um lugar só, a segunda tela
// tenderia a decidir sozinha, olhando se o campo está preenchido — e
// preenchido não é pago. Desde 11/09/2026 o selo não some ao pagar: fica
// riscado em cinza, e é o `recebido` DO SERVIDOR que decide isso.
//
// A distinção não é preciosismo: quem paga é o backend, com trava de índice
// único no banco, e ele também paga campos que chegaram por outro caminho (CPF
// digitado no checkout, telefone vindo do cadastro, dado corrigido pelo admin).
// Uma tela que adivinhasse riscaria o "+10" antes de a moeda existir — e o
// risco é justamente o recibo de que ela entrou.
//
// CUSTO: uma requisição por aba do perfil que mostre bônus. É o preço de o
// número ser verdadeiro; a alternativa barata é o número mentir.
import { useCallback, useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API;

const useCoinsPerfil = () => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const buscar = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setCarregando(false);
      return;
    }
    try {
      const r = await fetch(`${API_URL}api/usuario-editora/coins-perfil`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setDados(await r.json());
    } catch (_) {
      // Falhar aqui só apaga os "+10". O formulário continua inteiro e
      // salvável — nenhum campo depende deste dado para funcionar.
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    buscar();
  }, [buscar]);

  /**
   * Quantos coins o campo ainda paga. Zero quando já pagou — ou enquanto a
   * resposta não chegou.
   *
   * O zero durante o carregamento é escolha: é melhor o "+10" aparecer meio
   * segundo depois do que aparecer num campo já pago e sumir na cara da
   * pessoa, que leria isso como a recompensa sendo tirada dela.
   *
   * Campos premiados: os de `campos_perfil_premiados` no config do servidor,
   * mais a foto. Quem quer desenhar o estado RISCADO usa `premioDoCampo`.
   */
  const bonusDoCampo = useCallback(
    (campo) => {
      const item = dados?.itens?.find((i) => i.campo === campo);
      if (!item || item.recebido) return 0;
      return Math.max(0, Math.floor(Number(item.coins) || 0));
    },
    [dados]
  );

  /**
   * O prêmio inteiro do campo — `{ coins, recebido, minimo }` — ou `null`.
   *
   * `null` enquanto a resposta não chega, e não um "+N" provisório, pelo
   * mesmo motivo do `bonusDoCampo`: desenhar amarelo e riscar meio segundo
   * depois pareceria a recompensa sendo tirada. Depois de pago, `coins` é o
   * que ENTROU no saldo, não o preço de hoje.
   */
  const premioDoCampo = useCallback(
    (campo) => {
      const item = dados?.itens?.find((i) => i.campo === campo);
      if (!item) return null;
      const coins = Math.max(0, Math.floor(Number(item.coins) || 0));
      if (!coins) return null;
      return { coins, recebido: !!item.recebido, minimo: item.minimo || null };
    },
    [dados]
  );

  // A foto do avatar pode ter vindo do Lattes ou do WhatsApp, importada por um
  // comando do admin. Ela aparece na tela mas NÃO paga (o crédito olha a coluna
  // `image`, o envio da própria pessoa). Sem esta bandeira, a tela mostraria o
  // rosto da pessoa e, ao lado, um "+25" sem nenhuma explicação do que fazer.
  const fotoImportada = !!dados?.itens?.find((i) => i.campo === "foto")?.importada;

  return {
    itens: dados?.itens || [],
    aGanhar: Number(dados?.a_ganhar) || 0,
    totalPossivel: Number(dados?.total_possivel) || 0,
    carregando,
    bonusDoCampo,
    premioDoCampo,
    fotoImportada,
    recarregar: buscar,
  };
};

export default useCoinsPerfil;
